import { Component, computed, inject } from '@angular/core';
import {
    UIBannerComponent,
    UIButtonComponent,
    UIDialogComponent,
    UITooltipDirective,
    UINewThemeService,
    UISVGIconComponent,
    UIDialogButtonsComponent,
    UIComponentSizeDirective
} from '@bannerflow/ui';
import { ICreative } from '@domain/creativeset/creative/creative';
import { HeavyAssetsService } from '@studio/common/heavy-assets';
import { ICampaignStatus } from '@studio/domain/campaigns';
import { NavigationService } from '../../../shared/services/navigation.service';

@Component({
    imports: [
        UIButtonComponent,
        UIBannerComponent,
        UISVGIconComponent,
        UIDialogButtonsComponent,
        UITooltipDirective,
        UIComponentSizeDirective
    ],
    selector: 'publish-changes-dialog',
    templateUrl: './publish-changes-dialog.component.html',
    styleUrls: [
        './publish-changes-dialog.component.scss',
        './publish-changes-dialog.new.component.scss'
    ]
})
export class PublishChangesDialogComponent {
    private heavyAssetsService = inject(HeavyAssetsService);
    private navigationService = inject(NavigationService);
    private uiDialogComponent = inject(UIDialogComponent);
    private uiNewThemeService = inject(UINewThemeService);

    isNewUI = this.uiNewThemeService.isNewThemeEnabled;

    hasHeavyAssets = computed(() => this.computeHasHeavyAssets());
    heavyFileNames = computed(() => this.computeHeavyFileNames());

    campaigns: ICampaignStatus[] = [];
    creatives: ICreative[] = [];

    constructor() {
        this.campaigns = this.uiDialogComponent.config.data.campaigns as ICampaignStatus[];
        this.creatives = (this.uiDialogComponent.config.data.creatives as ICreative[]) ?? [];
    }

    cancel(): void {
        this.uiDialogComponent.dialogRef.close();
    }

    getCampaignLink(id: string): string {
        return this.navigationService.getCampaignLink(id);
    }

    publishChanges(): void {
        this.uiDialogComponent.config.data.confirmed = true;
        this.uiDialogComponent.dialogRef.close();
    }

    private computeHasHeavyAssets(): boolean {
        const heavyAssetMetadata = this.heavyAssetsService.heavyAssetsData();
        if (!heavyAssetMetadata || !Object.keys(heavyAssetMetadata).length) {
            return false;
        }
        return !!this.creatives.some(({ id }) => !!heavyAssetMetadata[id]?.length);
    }

    private computeHeavyFileNames(): string {
        const heavyAssetMetadata = this.heavyAssetsService.heavyAssetsData();
        const fileNames = new Set<string>();
        const limit = this.heavyAssetsService.heavyVideoSizeLimit();
        for (const creative of this.creatives) {
            const metadata = heavyAssetMetadata?.[creative.id];
            if (!metadata) {
                continue;
            }
            for (const { fileName, contentLength } of metadata) {
                if (contentLength > limit.bytes) {
                    fileNames.add(fileName);
                }
            }
        }
        return Array.from(fileNames).join(', ') ?? '';
    }
}
